import { Avatar, Box, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { ShoppingCart } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "../../../../hooks/useTranslation";
import { OrderItem } from "../../types";
import { CopiableChip } from "../../../../components/ui/CopiableChip";
import { SectionHeader } from "../../../../components/ui/SectionHeader";

type Props = {
  items: OrderItem[];
  currencyCode?: string;
};

export const OrderItemsTable = ({ items, currencyCode }: Props) => {
  const { translate } = useTranslation("pages.orderDetails");

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        backgroundColor: (theme) => theme.palette.customBackground.box,
      }}
    >
      <SectionHeader
        icon={<ShoppingCart sx={{ fontSize: 20, color: "primary.main" }} />}
        title={translate("orderItems.title")}
        color="primary"
        count={items.length}
      />

      <Stack spacing={1.5} sx={{ mt: 2 }}>
        {items.map((item) => {
          const receivedRatio =
            item.quantity > 0
              ? Math.min(100, (item.receivedQuantity / item.quantity) * 100)
              : 0;
          const isFullyReceived = item.receivedQuantity >= item.quantity;
          const asin = item.productCode.toUpperCase();

          return (
            <Paper
              key={item.id}
              variant="outlined"
              sx={{
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 2,
                borderRadius: 2,
                transition:
                  "box-shadow 0.15s ease, border-color 0.15s ease, background-color 0.15s ease",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: (theme) =>
                    alpha(theme.palette.text.primary, 0.03),
                },
              }}
            >
              <Tooltip title={translate("orderItems.viewOnAmazon")}>
                <Box
                  component="a"
                  href={`https://www.amazon.com/dp/${asin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "block",
                    flexShrink: 0,
                    borderRadius: 1.5,
                    overflow: "hidden",
                    lineHeight: 0,
                    transition: "opacity 0.15s ease",
                    "&:hover": { opacity: 0.8 },
                  }}
                >
                  <Avatar
                    variant="rounded"
                    src={item.imageUrl}
                    alt={item.productName}
                    sx={{ width: 64, height: 64 }}
                  />
                </Box>
              </Tooltip>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {item.productName}
                </Typography>
                <CopiableChip initialLabel={asin} />
              </Box>

              <Stack spacing={0} alignItems="flex-end" sx={{ minWidth: 90 }}>
                <Typography variant="body2" fontWeight={700} color="success.main">
                  {item.unitPrice.toFixed(2)} {currencyCode}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: "0.625rem",
                    textTransform: "uppercase",
                    letterSpacing: 0.3,
                  }}
                >
                  {translate("orderItems.price")}
                </Typography>
              </Stack>

              <Stack spacing={0.375} sx={{ minWidth: 100 }}>
                <Stack direction="row" alignItems="baseline" spacing={0.375}>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color={isFullyReceived ? "success.main" : "warning.main"}
                  >
                    {item.receivedQuantity}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    / {item.quantity}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    width: "100%",
                    height: 4,
                    borderRadius: 999,
                    backgroundColor: "action.hover",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      borderRadius: 999,
                      width: `${receivedRatio}%`,
                      backgroundColor: isFullyReceived
                        ? "success.main"
                        : "warning.main",
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{
                    fontSize: "0.625rem",
                    textTransform: "uppercase",
                    letterSpacing: 0.3,
                  }}
                >
                  {translate("orderItems.quantity")}
                </Typography>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Paper>
  );
};
